import type { TLDefaultColor, TLTheme } from "@tldraw/tlschema";

// Planroom's drawing palette. Each named colour is one base value; the tints the
// shapes use (fills, notes, highlights) are derived from it.

const BASE: Record<string, string> = {
  black: "#1d1d1d",
  grey: "#8a8f98",
  "light-violet": "#d29de8",
  violet: "#8b4fc9",
  blue: "#3a63d6",
  "light-blue": "#5fb0e8",
  yellow: "#f1ae32",
  orange: "#e2702b",
  green: "#2e9b67",
  "light-green": "#64c26f",
  "light-red": "#f0828a",
  red: "#d6453f",
  white: "#ffffff",
};

function mix(hex: string, toward: string, amount: number) {
  const parse = (h: string) =>
    [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
  const [a, b] = [parse(hex), parse(toward)];
  return `#${a
    .map((v, i) =>
      Math.round(v + (b[i] - v) * amount)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;
}

function tints(solid: string): TLDefaultColor {
  return {
    solid,
    semi: mix(solid, "#ffffff", 0.75),
    pattern: mix(solid, "#ffffff", 0.2),
    fill: mix(solid, "#ffffff", 0.85),
    linedFill: mix(solid, "#ffffff", 0.7),
    frameHeadingStroke: mix(solid, "#ffffff", 0.4),
    frameHeadingFill: mix(solid, "#ffffff", 0.9),
    frameStroke: mix(solid, "#ffffff", 0.4),
    frameFill: mix(solid, "#ffffff", 0.95),
    frameText: mix(solid, "#000000", 0.3),
    noteFill: mix(solid, "#ffffff", 0.55),
    noteText: "#1d1d1d",
    highlightSrgb: mix(solid, "#ffffff", 0.45),
    highlightP3: mix(solid, "#ffffff", 0.45),
  };
}

// Notes and highlights keep the values existing boards were drawn with
// (measured from the earlier tldraw canvas), so they look unchanged.
const NOTE_FILL: Record<string, string> = {
  black: "#fce19c",
  grey: "#c0cad3",
  "light-violet": "#dfb0f9",
  violet: "#db91fd",
  blue: "#8aa3ff",
  "light-blue": "#9bc4fd",
  yellow: "#fed49a",
  orange: "#faa475",
  green: "#6fc896",
  "light-green": "#98d08a",
  "light-red": "#f7a5a1",
  red: "#fc8282",
  white: "#ffffff",
};
const HIGHLIGHT_P3: Record<string, [number, number, number]> = {
  black: [0.972, 0.8205, 0.05],
  grey: [0.8163, 0.9023, 0.9416],
  "light-violet": [0.9676, 0.5652, 0.9999],
  violet: [0.7469, 0.5089, 0.9995],
  blue: [0.308, 0.6632, 0.9996],
  "light-blue": [0.1512, 0.9414, 0.9996],
  yellow: [0.972, 0.8705, 0.05],
  orange: [0.9988, 0.6905, 0.266],
  green: [0.2536, 0.984, 0.7981],
  "light-green": [0.563, 0.9495, 0.3857],
  "light-red": [0.9988, 0.5301, 0.6397],
  red: [0.9992, 0.4376, 0.45],
  white: [1, 1, 1],
};
const hex = (rgb: number[]) =>
  `#${rgb.map((v) => Math.round(v * 255).toString(16).padStart(2, "0")).join("")}`;

const colors = Object.fromEntries(
  Object.entries(BASE).map(([k, v]) => {
    const p3 = HIGHLIGHT_P3[k];
    return [
      k,
      {
        ...tints(v),
        noteFill: NOTE_FILL[k],
        highlightP3: `color(display-p3 ${p3.join(" ")})`,
        // Close enough where wide-gamut colour is unavailable.
        highlightSrgb: hex(p3),
      },
    ];
  }),
);
const ui = {
  text: "#1d1d1d",
  background: "#f7f6f3",
  negativeSpace: "#ffffff",
  solid: "#fcfffe",
  cursor: "#1d1d1d",
  noteBorder: "#e3e2de",
  snap: "#e24f4f",
  selectionStroke: "#2f80ed",
  selectionFill: "rgba(47, 128, 237, 0.08)",
  brushFill: "rgba(47, 128, 237, 0.06)",
  brushStroke: "rgba(47, 128, 237, 0.35)",
  selectedContrast: "#ffffff",
  laser: "#ff2133",
};

export const planroomTheme = {
  id: "default",
  fontSize: 16,
  lineHeight: 1.35,
  strokeWidth: 2,
  fonts: {
    draw: { fontFamily: "'Shantell Sans', 'IBM Plex Sans', sans-serif" },
    sans: { fontFamily: "'IBM Plex Sans', 'DM Sans', sans-serif" },
    serif: { fontFamily: "'IBM Plex Serif', serif" },
    mono: { fontFamily: "'IBM Plex Mono', monospace" },
  },
  colors: {
    light: { ...ui, ...colors },
    dark: { ...ui, ...colors },
  },
} as unknown as TLTheme;
